import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "./Reveal";

const IMG_HERO =
    "https://images.unsplash.com/photo-1723639905934-744ae9bbc3a0?q=80&w=1600&auto=format&fit=crop";
const IMG_TEXTURE =
    "https://images.unsplash.com/photo-1652862730507-32988eb23a67?q=80&w=2000&auto=format&fit=crop";

const LINES = [
    { text: "Kaca, Aluminium", italic: false },
    { text: "& Desain Interior", italic: true },
    { text: "dengan Presisi.", italic: false },
];

export default function Hero({ onNavigate }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
    const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-38%"]);
    const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

    return (
        <section
            id="beranda"
            ref={ref}
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20 lg:pt-24"
        >
            <div className="absolute inset-0">
                <img
                    src={IMG_TEXTURE}
                    alt=""
                    className="h-full w-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/80 to-ink" />
                <div className="fluted-lines absolute inset-0" />
            </div>

            <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-8 lg:px-10">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="flex items-center gap-4"
                    >
                        <span className="h-px w-10 bg-gold/70" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                            Aplikator Kaca Aluminium · Desain Interior
                        </span>
                    </motion.div>

                    <h1 className="mt-8 font-serif text-[clamp(2.7rem,7.8vw,6rem)] font-medium leading-[1.02] tracking-tight text-white">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${
                                        line.italic
                                            ? "italic text-chrome"
                                            : ""
                                    }`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: "0%" }}
                                    transition={{
                                        duration: 1.05,
                                        ease: EASE,
                                        delay: 0.2 + i * 0.13,
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 lg:text-lg"
                    >
                        Melayani pemasangan kanopi, kusen, sliding &amp; folding
                        door, fasad, partisi kaca, shower glass, hingga
                        pendampingan desain interior untuk hunian dan bangunan
                        komersial — rapi, presisi, dan tepat waktu.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-portfolio-cta"
                            onClick={() => onNavigate("#portofolio")}
                            className="rounded-full bg-gold px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#e5c14e]"
                        >
                            Lihat Portofolio
                        </button>
                        <a
                            href="https://wa.me/628113030918?text=Halo%2C%20saya%20ingin%20konsultasi%20proyek%20kaca%20aluminium%20%2F%20desain%20interior"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-cta"
                            className="rounded-full border border-white/25 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.18em] text-white transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
                        >
                            Chat via WhatsApp
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.55 }}
                    className="relative lg:col-span-5"
                    style={{ perspective: 1200 }}
                >
                    <div
                        className="img-frame relative aspect-[4/5] max-h-[70vh] w-full"
                        style={{ transform: "rotateX(0.01deg)" }}
                    >
                        <motion.img
                            src={IMG_HERO}
                            alt="Pintu folding glass aluminium presisi"
                            className="absolute inset-0 h-full w-full scale-110 object-cover"
                            style={{ y: imgY, rotate: imgRotate }}
                        />
                    </div>

                    <motion.div
                        style={{ y: cardY }}
                        className="glass-panel absolute -bottom-6 -left-4 max-w-[280px] p-5 lg:-left-10"
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                            Spesifikasi Kerja
                        </div>
                        <div className="mt-3 space-y-2 font-mono text-[11px] leading-relaxed text-zinc-300">
                            <div className="flex justify-between gap-6">
                                <span className="text-zinc-500">MATERIAL</span>
                                <span className="text-right">
                                    ALUMINIUM · TEMPERED GLASS
                                </span>
                            </div>
                            <div className="flex justify-between gap-6">
                                <span className="text-zinc-500">KETEBALAN</span>
                                <span className="text-right">5 — 12 MM</span>
                            </div>
                            <div className="flex justify-between gap-6">
                                <span className="text-zinc-500">LINGKUP</span>
                                <span className="text-right">
                                    RUMAH · RUKO · KANTOR
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            <div className="absolute bottom-8 left-6 z-10 hidden items-center gap-4 lg:flex lg:left-10">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Scroll
                </span>
                <span className="h-px w-14 animate-scroll-cue bg-gold/80" />
            </div>
        </section>
    );
}
